import { Component, Inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";

import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { PlatformUtilsService } from "@bitwarden/common/abstractions/platformUtils.service";
import { DeviceApprovalRequestResponse } from "@bitwarden/common/models/response/device-approval-request.response";

import { DeviceApprovalService } from "../../services/DataService/device-trust/device-approval.service";

export interface PendingDeviceApprovalDialogData {
  request: DeviceApprovalRequestResponse;
  fingerprintPhrase: string;
}

export type PendingDeviceApprovalDialogResult = "approved" | "denied" | "dismissed";

/**
 * TDE new-device approval, approver side (GOOGLE_SSO_PLAN.md, Phase 3). Opened by
 * `DeviceApprovalWatcherService` on an already-trusted, unlocked device when another device
 * asks to be approved. Approve stays disabled until the user confirms the fingerprint phrase
 * matches the one on the requesting device — that check is what stops a compromised server
 * from swapping in its own public key and receiving the UserKey.
 */
@Component({
  selector: "app-pending-device-approval-dialog",
  templateUrl: "pending-device-approval-dialog.component.html",
})
export class PendingDeviceApprovalDialogComponent {
  confirmedFingerprint = false;
  submitting = false;

  constructor(
    public dialogRef: MatDialogRef<
      PendingDeviceApprovalDialogComponent,
      PendingDeviceApprovalDialogResult
    >,
    @Inject(MAT_DIALOG_DATA) public data: PendingDeviceApprovalDialogData,
    private deviceApprovalService: DeviceApprovalService,
    private platformUtilsService: PlatformUtilsService,
    private i18nService: I18nService,
    private logService: LogService,
  ) {}

  async approve() {
    if (!this.confirmedFingerprint || this.submitting) {
      return;
    }
    await this.respond("approved", () => this.deviceApprovalService.approve(this.data.request));
  }

  async deny() {
    if (this.submitting) {
      return;
    }
    await this.respond("denied", () => this.deviceApprovalService.deny(this.data.request.id));
  }

  dismiss() {
    this.dialogRef.close("dismissed");
  }

  private async respond(result: PendingDeviceApprovalDialogResult, action: () => Promise<void>) {
    this.submitting = true;
    try {
      await action();
      this.platformUtilsService.showToast(
        "success",
        null,
        this.i18nService.t(result === "approved" ? "deviceApproved" : "deviceDenied"),
      );
      this.dialogRef.close(result);
    } catch (e) {
      this.logService.error(e);
      this.platformUtilsService.showToast("error", null, this.i18nService.t("errorOccurred"));
    } finally {
      this.submitting = false;
    }
  }
}
